import { useCallback, useEffect } from 'react';
import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';

const HEADING_BUTTON_SELECTOR = '.doran-calendar__heading-btn';
const ACTIVE_HEADING_SELECTOR = '.doran-calendar__heading-btn--active';
const PICKER_OPTION_SELECTOR = '.doran-picker-option';

const normalizeDigits = (value: string): string =>
    value
        .replace(/[۰-۹]/g, (digit) =>
            String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)),
        )
        .replace(/[٠-٩]/g, (digit) =>
            String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)),
        );

const extractYear = (element: Element | null): number | null => {
    if (!element) return null;

    const normalized = normalizeDigits(element.textContent ?? '');
    const match = normalized.match(/\b(1[0-9]{3})\b/);

    return match ? Number(match[1]) : null;
};

const getCalendar = (container: HTMLElement | null): HTMLElement | null =>
    container?.querySelector<HTMLElement>('.doran-calendar') ?? null;

const getHeadingButtons = (calendar: HTMLElement): HTMLButtonElement[] =>
    Array.from(
        calendar.querySelectorAll<HTMLButtonElement>(HEADING_BUTTON_SELECTOR),
    );

const getYearHeading = (calendar: HTMLElement): HTMLButtonElement | null =>
    getHeadingButtons(calendar).find((button) => extractYear(button) !== null) ??
    null;

const getMonthHeading = (calendar: HTMLElement): HTMLButtonElement | null =>
    getHeadingButtons(calendar).find((button) => extractYear(button) === null) ??
    null;

const runAfterPanelRender = (callback: () => void): number =>
    window.requestAnimationFrame(() => window.requestAnimationFrame(callback));

const scrollYearIntoView = (calendar: HTMLElement, year: number): void => {
    const option = Array.from(
        calendar.querySelectorAll<HTMLElement>(PICKER_OPTION_SELECTOR),
    ).find((item) => extractYear(item) === year);

    if (!option) return;

    let parent = option.parentElement;
    while (parent && parent !== calendar) {
        const style = window.getComputedStyle(parent);
        const canScroll =
            parent.scrollHeight > parent.clientHeight &&
            /(auto|scroll)/.test(style.overflowY);

        if (canScroll) {
            const parentRect = parent.getBoundingClientRect();
            const optionRect = option.getBoundingClientRect();
            const target =
                parent.scrollTop +
                (optionRect.top - parentRect.top) -
                parent.clientHeight / 2 +
                optionRect.height / 2;
            parent.scrollTop = Math.max(0, target);
            return;
        }

        parent = parent.parentElement;
    }

    option.scrollIntoView({ block: 'center', inline: 'nearest' });
};

type UseDoranYearFirstNavigationArgs = {
    containerRef: RefObject<HTMLDivElement>;
    open: boolean;
    enabled: boolean;
    currentYear: number;
};

/**
 * Adds the registration-friendly year → month → day drill-down on top of
 * Doran's own dropdown panels without reimplementing the calendar itself.
 */
export const useDoranYearFirstNavigation = ({
    containerRef,
    open,
    enabled,
    currentYear,
}: UseDoranYearFirstNavigationArgs) => {
    useEffect(() => {
        if (!open || !enabled) return;

        const frame = window.requestAnimationFrame(() => {
            const calendar = getCalendar(containerRef.current);
            if (!calendar) return;

            const yearHeading = getYearHeading(calendar);
            if (!yearHeading) return;

            if (!yearHeading.classList.contains('doran-calendar__heading-btn--active')) {
                yearHeading.click();
            }

            runAfterPanelRender(() => scrollYearIntoView(calendar, currentYear));
        });

        return () => window.cancelAnimationFrame(frame);
    }, [containerRef, currentYear, enabled, open]);

    return useCallback(
        (event: ReactMouseEvent<HTMLDivElement>) => {
            if (!enabled) return;

            const target = event.target as Element | null;
            const option = target?.closest(PICKER_OPTION_SELECTOR);
            if (!option) return;

            const calendar = getCalendar(containerRef.current);
            if (!calendar) return;

            const activeHeading = calendar.querySelector<HTMLButtonElement>(
                ACTIVE_HEADING_SELECTOR,
            );

            // Only intercept a year choice. Month choices are left entirely to
            // Doran, which naturally returns to the day grid afterwards.
            if (extractYear(activeHeading) === null) return;

            runAfterPanelRender(() => {
                const currentCalendar = getCalendar(containerRef.current);
                if (!currentCalendar) return;

                const monthHeading = getMonthHeading(currentCalendar);
                if (!monthHeading) return;

                const nextActiveHeading =
                    currentCalendar.querySelector<HTMLButtonElement>(
                        ACTIVE_HEADING_SELECTOR,
                    );

                // Newer Doran builds can advance to month automatically.
                if (nextActiveHeading === monthHeading) return;

                // If the year panel is still open, close it through Doran's
                // own heading control before opening the month panel.
                if (extractYear(nextActiveHeading) !== null) {
                    nextActiveHeading?.click();
                    window.requestAnimationFrame(() => monthHeading.click());
                    return;
                }

                monthHeading.click();
            });
        },
        [containerRef, enabled],
    );
};
